import i18next from "@app/i18n";

const enResources = {
    see_suggestion_text: "View AI suggestion",
    generating_suggestion: "Generating...",
    suggestion_error: "AI could not generate meaningful answer",
    table_empty_state_title: "No questions found",
    table_empty_state_subtitle: "Modify your filters or add questions",
    table_header_question: "Question",
    table_header_answer: "Answer",
    table_header_ai_answer: "AI suggested answer",
    table_header_status: "Status",
    table_header_owner: "Assignee",
    table_header_evidence: "Evidence",
    table_header_status_dropdown_item_todo: "Todo",
    table_header_status_dropdown_item_validated: "Validated",
    table_header_status_dropdown_item_in_progress: "In progress",
    table_header_groups: "Groups",

    table_owner_assign_no_owner: "Unassigned",

    table_question_type_text: "Text",
    table_question_type_value: "Number",
    table_question_type_single_select: "Single select",
    table_question_type_multi_select: "Multiple select",

    table_header_checkbox_disabled:
        "All questions are validated and cannot be modified",
    question_checkbox_disabled:
        "You can't edit a question that has been validated",
    question_table_action_selection: "{{count}} questions selected",
    question_table_action_select_action: "Select action",
    question_table_action_assign_owner: "Assign owner ({{count}})",
    question_table_action_accept_ai_answer: "Accept AI suggestion ({{count}})",
    question_table_action_validate_answer: "Validate answer ({{count}})",
    question_table_action_apply: "Apply",
    question_table_action_assign_success: "Data updated successfully",
    question_table_action_assign_error: "Error updating data",
    question_table_action_delete: "Delete ({{count}})",
    question_table_action_delete_success: "Questions deleted successfully",
    question_table_action_delete_error: "Error deleting questions",
    create_questions_modal_success_message: "Question created successfully",
    create_questions_modal_error_message:
        "There was an error while creating the question",

    create_questions_modal_trigger: "Add question",
    create_questions_modal_title: "Create new question",
    create_questions_modal_confirm: "Create",
    update_questions_modal_confirm: "Update",
    create_questions_modal_cancel: "Cancel",
    create_questions_modal_confirm_disabled_tooltip:
        "Please fill type, name and options if necessary",
    create_questions_modal_add_question: "Add question",
    create_questions_modal_create_more_questions: "Create more",
    create_questions_modal_depends_on_other_question:
        "Link question to a parent question",
    create_questions_modal_depends_on_select_question_label: "Parent question",
    create_questions_modal_depends_on_select_question_description:
        "Parent questions must have the answer type single-select or multi-select",
    create_questions_modal_depends_on_select_answers_label:
        "Required answers to parent question",
    create_questions_modal_question_type_label: "Question type",
    create_questions_modal_question_name_label: "Question",
    create_questions_modal_question_name_placeholder:
        "Example: Total CO2 emissions in the current year",
    create_questions_modal_question_description_label: "Description",
    create_questions_modal_question_type_dd_label: "Answer type",
    create_questions_modal_question_type_dd_item_label_text: "Text",
    create_questions_modal_question_type_dd_item_label_value: "Number",
    create_questions_modal_question_type_dd_item_label_single_select:
        "Single choice",
    create_questions_modal_question_type_dd_item_label_multi_select:
        "Multiple choice",

    create_questions_modal_options_block_label: "Answer option {{ index }}",
    create_questions_modal_options_block_add_option: "Add option",
    create_questions_modal_options_block_proof_label: "Document",
    create_questions_modal_options_block_proof_dropdown_item: "Unknown",
    create_questions_modal_options_block_proof_dropdown_item_ALLOWED:
        "Optional",
    create_questions_modal_options_block_proof_dropdown_item_REQUIRED:
        "Required",
    create_questions_modal_options_block_proof_dropdown_item_NOT_ALLOWED:
        "Not allowed",

    create_questions_modal_enable_na_option_label:
        'Allow respondents to answer "NA"',
    create_questions_modal_require_answer_label: "Require answer",
    create_questions_modal_question_unit_label: "Unit",
    create_questions_modal_question_label: "Question",
    create_questions_modal_answer_label: "Answer",
    en: "English",
    fr: "French",
    edit_question_modal_title: "Edit question",
    question_dependent_tooltip:
        "This question appears based on the answer to a previous question",
    regenerate_answer_button: "Unable to generate",
    evidence_files: "{{count}} document(s)",
    evidence_files_one: "{{count}} document",
    evidence_files_other: "{{count}} documents",
    document_citations: "{{count}} suggested document(s)",
    document_citations_one: "{{count}} suggested document",
    document_citations_other: "{{count}} suggested documents",
    copy_to_clipboard_tooltip: "Copy to clipboard",
    insert_suggestion_tooltip_title: "Accept AI suggestion",
    insert_suggestion_tooltip_replace_answer: "Replace answer",
    insert_suggestion_tooltip_add_documents: "Add suggested documents",
    regenerate_suggestion: "Regenerate suggestion",
    generate_suggestion: "Generate suggestion",
    toast_error_message: "Error updating questions order",
    question_table_action_set_irrelevant: "Set as non relevant ({{count}})",
} as const;

type ResourcesKeys = keyof typeof enResources;

const frResources: Record<ResourcesKeys, string> = {
    see_suggestion_text: "Voir la suggestion AI",
    generating_suggestion: "Génération...",
    suggestion_error: "Impossible de générer une réponse pertinente",
    table_empty_state_title: "Aucune question trouvée",
    table_empty_state_subtitle: "Modifier vos filtres ou ajouter des questions",
    table_header_question: "Question",
    table_header_answer: "Réponse",
    table_header_ai_answer: "Réponse suggérée par AI",
    table_header_status: "Status",
    table_header_owner: "Personne assignée",
    table_header_evidence: "Justificatifs",
    table_header_status_dropdown_item_todo: "À faire",
    table_header_status_dropdown_item_validated: "Validé",
    table_header_status_dropdown_item_in_progress: "En cours",
    table_header_groups: "Groupes",
    table_owner_assign_no_owner: "Non assigné",

    table_question_type_text: "Texte",
    table_question_type_value: "Nombre",
    table_question_type_single_select: "Choix unique",
    table_question_type_multi_select: "Choix multiple",

    table_header_checkbox_disabled:
        "Toutes les questions sont validées et ne peuvent pas être modifiées",
    question_checkbox_disabled:
        "Vous ne pouvez pas modifier une question qui a été validée",
    question_table_action_selection: "{{count}} questions sélectionnées",
    question_table_action_select_action: "Sélectionner une action",
    question_table_action_assign_owner: "Assigner un propriétaire ({{count}})",
    question_table_action_accept_ai_answer: "Accepter la suggestion de l'IA ({{count}})",
    question_table_action_validate_answer: "Valider la réponse ({{count}})",
    question_table_action_apply: "Appliquer",
    question_table_action_assign_success: "Données mises à jour avec succès",
    question_table_action_assign_error:
        "Erreur lors de la mise à jour des données",
    question_table_action_delete: "Supprimer ({{count}})",
    question_table_action_delete_success: "Questions supprimées avec succès",
    question_table_action_delete_error:
        "Erreur lors de la suppression des questions",
    question_dependent_tooltip:
        "Cette question apparaît en fonction de la réponse à une question précédente",
    create_questions_modal_success_message: "Question créée avec succès",
    create_questions_modal_error_message:
        "Il y a eu une erreur lors de la création de la question",

    create_questions_modal_trigger: "Ajouter des questions",
    create_questions_modal_title: "Ajouter une question",
    create_questions_modal_confirm: "Créer",
    update_questions_modal_confirm: "Modifier",
    create_questions_modal_cancel: "Annuler",
    create_questions_modal_confirm_disabled_tooltip:
        "Veuillez remplir type, nom et options si nécessaire",
    create_questions_modal_add_question: "Rajouter une question",
    create_questions_modal_create_more_questions:
        "Créer une autre question de suite",
    create_questions_modal_depends_on_other_question:
        "Ajouter une logique conditionnelle",
    create_questions_modal_depends_on_select_question_label: "Question parente",
    create_questions_modal_depends_on_select_question_description:
        "Les questions parentes doivent être des questions à choix unique ou multiple.",
    create_questions_modal_depends_on_select_answers_label: "Réponses requises",
    create_questions_modal_question_type_label: "Type de question",
    create_questions_modal_question_name_label: "Question",
    create_questions_modal_question_description_label: "Description",
    create_questions_modal_question_name_placeholder:
        "Exemple: Total des émissions de carbone en l'année en cours",
    create_questions_modal_question_type_dd_label: "Type de réponse",
    create_questions_modal_question_type_dd_item_label_text: "Texte",
    create_questions_modal_question_type_dd_item_label_value: "Nombre",
    create_questions_modal_question_type_dd_item_label_single_select:
        "Sélection unique",
    create_questions_modal_question_type_dd_item_label_multi_select:
        "Choix multiple",

    create_questions_modal_options_block_label: "Choix de réponse {{ index }}",
    create_questions_modal_options_block_add_option: "Ajouter une option",
    create_questions_modal_options_block_proof_label: "Document",
    create_questions_modal_options_block_proof_dropdown_item: "Inconnu",
    create_questions_modal_options_block_proof_dropdown_item_ALLOWED:
        "Optionnel",
    create_questions_modal_options_block_proof_dropdown_item_REQUIRED:
        "Obligatoire",
    create_questions_modal_options_block_proof_dropdown_item_NOT_ALLOWED:
        "Non requis",

    create_questions_modal_enable_na_option_label: 'Autoriser la réponse "N/A"',
    create_questions_modal_require_answer_label: "La réponse est obligatoire",
    edit_question_modal_title: "Modifier la question",
    create_questions_modal_question_label: "Question",
    create_questions_modal_answer_label: "Réponse",
    en: "Anglais",
    fr: "Français",
    create_questions_modal_question_unit_label: "Unité",
    regenerate_answer_button: "Impossible de générer",
    evidence_files: "{{count}} document(s)",
    evidence_files_one: "{{count}} document",
    evidence_files_other: "{{count}} documents",
    document_citations: "{{count}} document(s) suggéré(s)",
    document_citations_one: "{{count}} document suggéré",
    document_citations_other: "{{count}} documents suggérés",
    copy_to_clipboard_tooltip: "Copier dans le presse-papiers",
    insert_suggestion_tooltip_title: "Accepter la suggestion AI",
    insert_suggestion_tooltip_replace_answer: "Remplacer la réponse",
    insert_suggestion_tooltip_add_documents: "Ajouter les documents suggérés",
    regenerate_suggestion: "Régénérer la suggestion",
    generate_suggestion: "Générer une suggestion",
    toast_error_message: "Erreur lors de la mise à jour des questions",
    question_table_action_set_irrelevant: "Marquer comme non pertinent ({{count}})",
};

export const RESOURCE_NAME = "OverviewTab";
export const keys = Object.fromEntries(
    Object.keys(enResources).map((key) => [key, key]),
) as Record<ResourcesKeys, ResourcesKeys>;

i18next.addResourceBundle("en", RESOURCE_NAME, enResources);
i18next.addResourceBundle("fr", RESOURCE_NAME, frResources);
